// ============================================================================
// Game Provider Configuration
// Frontend only configures: icon mapping + auto-generated tagline
// All game data (name, status, etc.) comes from backend API: GET /game-providers
// ============================================================================

export interface GameProvider {
  id: string;
  code?: string; // Provider code from API (e.g., "bluedragon")
  name: string;
  tagline: string;
  icon: string; // Path to icon image (e.g., "/icons/vegasx.jpg")
  /** Status from server: active = normal, inactive = COMING SOON, maintenance = under maintenance */
  status?: 'active' | 'inactive' | 'maintenance';
  /** Deposit type: 'auto' = instant, 'manual' = staff processes (5min-2hrs) */
  depositType?: 'auto' | 'manual';
  /** Admin badge on card; omit or empty = hidden */
  clientTag?: string | null;
  /** Per-provider min deposit from admin config (USD); undefined = use fallback */
  minDeposit?: number;
  /** Public health snapshot (GET /game-providers/health); set only when that call succeeds */
  healthStatus?: 'healthy' | 'out_of_sync' | 'down_risk' | 'na';
  /** false = provider is down: deposits closed and Play replaced by "Under maintenance" */
  depositsOpen?: boolean;
}

// ============================================================================
// Icon Mapping
// Maps provider code → icon path. If not found, uses default icon.
// To add a new game icon: add entry here and place icon file in /public/icons/
// ============================================================================

const ICON_MAP: Record<string, string> = {
  'vegasx': '/icons/vegasx.jpg',
  'bluedragon': '/icons/blue dragon.jpg',
  'firekirin': '/icons/fire kirin.jpg',
  'gamevault': '/icons/game vault.jpg',
  'pandamaster': '/icons/panda.png',
  'goldentreasure': '/icons/golden treasure.jpg',
  'goldrush': '/icons/golden treasure.jpg',
  'ultrapanda': '/icons/utrapanda.jpg',
  'vblink777': '/icons/vblink.jpg',
  'vblink': '/icons/vblink.jpg',
  'egame99': '/icons/egame.jpg',
  'egame': '/icons/egame.jpg',
  'sweeps': '/icons/sweeps.png',
  'lasvegassweeps': '/icons/sweeps.png',
  'cashier': '/icons/vegasx.jpg',
  'royalspin': '/icons/sweeps.png',
  'aceplay': '/icons/egame.jpg',
  'riverpay': '/icons/riverpay.jpg',
  'cashfrenzy': '/icons/cashfrenzy.png',
  'gameroom': '/icons/gameroom777.png',
  'mrallinone': '/icons/mrallinone.png',
  'mafia': '/icons/mafia.jpeg',
  'mafia77777': '/icons/mafia.jpeg',
  'vegasroll': '/icons/vegasroll.png',
  'mwterminal': '/icons/mkterminal.jpeg',
  'osterminal': '/icons/orionstart.jpeg',
  'openjack2win': '/icons/jack2win.jpeg',
  'galaxyworld': '/icons/galaxyworld.jpg',
  'megaspin': '/icons/megaspin.jpg',
  'vegas7games': '/icons/vegas7games.jpg',
  'glamorspin': '/icons/glamour_spin.jpg',
  'juwa': '/icons/juwa777.jpeg',
  'goldendragon': '/icons/golden_dragon.jpg',
  'noble777': '/icons/noble777.png',
};

export const DEFAULT_PROVIDER_ICON = '/icons/egame.jpg';

/**
 * Gets icon path for a provider code. Returns default icon if not configured.
 */
export function getIconPathForProvider(code: string): string {
  const normalizedCode = code.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
  const path = ICON_MAP[normalizedCode] || DEFAULT_PROVIDER_ICON;
  // Paths may include spaces ("blue dragon.jpg") — encode so <img src> loads reliably.
  return encodeURI(path);
}

/**
 * Whether a provider has a dedicated icon configured (vs. the generic fallback).
 * Used to decide between real tile art and the Cosmos nebula fallback.
 */
export function hasIconForProvider(code: string | undefined | null): boolean {
  if (!code) return false;
  const normalizedCode = code.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
  return normalizedCode in ICON_MAP;
}

/**
 * Gallery art shown in the game sheet (three 16:9-ish tiles), served from /public/games/<folder>/{1,2,3}.webp.
 * Games missing here fall back to their icon in each tile.
 * Sources (400×225 WebP q75, centre-cropped to 16:9 — the widest tile is ~390 device px). Owner's order: Google Play screenshots first
 * (copycat listings that carry the game's branding are accepted), else art drawn from the game's icon.
 *   Google Play (app id):
 *     ultrapanda  com.ultrapanda.slots                                   firekirin   com.aub.firekirin.online.sweepstakes.slots.fire.kirin.online
 *     gamevault   com.gmvltinlsn.csn                                     juwa        com.casinojuwagame.juwa777
 *     osterminal  com.orionstars.casino.online.orion.casino              vblink      com.vblnk.rlmoney.skdjt
 *     pandamaster com.pandamaster777.slots.panda.master                  cashfrenzy  slots.pcg.casino.games.free.android
 *     sweeps      com.vegas.sweeps.online.casino.vegassweeps777          gameroom    com.gameroom.gameroom777.cash.slots
 *     mafia       com.mafia777.mafia.online.casinoslots                  mwterminal  com.milkyway777.slotscasino.win.cash
 *   Drawn from the icon (no Play listing with matching landscape art): vegasx, bluedragon, goldentreasure, egame,
 *     riverpay, mrallinone, vegasroll, galaxyworld, megaspin, vegas7games, glamorspin, goldendragon, noble777, openjack2win.
 * Browsers cache these by URL: when replacing a set, use new file names.
 */
const gallery = (folder: string): readonly string[] => [1, 2, 3].map((i) => `/games/${folder}/${i}.webp`);

const GALLERY_MAP: Record<string, readonly string[]> = {
  ultrapanda: gallery('ultrapanda'),
  firekirin: gallery('firekirin'),
  gamevault: gallery('gamevault'),
  juwa: gallery('juwa'),
  osterminal: gallery('osterminal'),
  vblink: gallery('vblink'),
  vblink777: gallery('vblink'),
  pandamaster: gallery('pandamaster'),
  cashfrenzy: gallery('cashfrenzy'),
  sweeps: gallery('sweeps'),
  lasvegassweeps: gallery('sweeps'),
  gameroom: gallery('gameroom'),
  mafia: gallery('mafia'),
  mafia77777: gallery('mafia'),
  mwterminal: gallery('mwterminal'),
  vegasx: gallery('vegasx'),
  cashier: gallery('vegasx'), // VegasX's live provider code
  bluedragon: gallery('bluedragon'),
  goldentreasure: gallery('goldentreasure'),
  goldrush: gallery('goldentreasure'),
  egame: gallery('egame'),
  egame99: gallery('egame'),
  riverpay: gallery('riverpay'),
  mrallinone: gallery('mrallinone'),
  vegasroll: gallery('vegasroll'),
  galaxyworld: gallery('galaxyworld'),
  megaspin: gallery('megaspin'),
  vegas7games: gallery('vegas7games'),
  glamorspin: gallery('glamorspin'),
  goldendragon: gallery('goldendragon'),
  noble777: gallery('noble777'),
  openjack2win: gallery('openjack2win'),
};

/** Gallery images for a provider code (same normalisation as the icon map); empty when it has none. */
export function getGalleryForProvider(code: string | undefined | null): string[] {
  if (!code) return [];
  const normalizedCode = code.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
  return [...(GALLERY_MAP[normalizedCode] ?? [])];
}

/**
 * Build a unique icon list from provider codes and always include fallback icon.
 * Useful for first-load prefetch so offline screen has ready assets.
 */
export function getProviderIconPaths(codes: Array<string | undefined | null>): string[] {
  const unique = new Set<string>();
  codes.forEach((code) => {
    if (!code) return;
    unique.add(getIconPathForProvider(code));
  });
  unique.add(DEFAULT_PROVIDER_ICON);
  return Array.from(unique);
}

// ============================================================================
// Auto-generated tagline for providers without explicit config
// ============================================================================

const taglineTemplates = [
  'Premium Gaming Experience',
  'Where Winners Play',
  'Your Ultimate Gaming Destination',
  'Experience the Thrill',
  'Play and Win Big',
  'The Best Gaming Platform',
  'Unleash Your Luck',
  'Gaming Excellence',
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

function getTaglineForProvider(code: string, name: string): string {
  const hash = hashString(code.toLowerCase() + name.toLowerCase());
  return taglineTemplates[hash % taglineTemplates.length];
}

// ============================================================================
// Map API response → GameProvider component format
// ============================================================================

export interface GameProviderApiItem {
  id: string;
  code: string;
  name: string;
  status: 'active' | 'inactive' | 'maintenance';
  displayOrder?: number;
  clientTag?: string | null;
  minDeposit?: number; // per-provider admin config (returned by GET /game-providers)
  /** Admin "Play inside the app" (GET /game-providers); Play itself reads the game wallet's `game.playInFrame`. */
  playInFrame?: boolean;
  config?: {
    depositType?: 'auto' | 'manual';
  };
}

/**
 * Maps a single API provider item to a GameProvider for UI rendering.
 * Uses icon map for icons, auto-generates the tagline.
 * All data (id, name, status) comes from API.
 */
export const mapApiProviderToComponent = (apiProvider: GameProviderApiItem): GameProvider | null => {
  // Maintenance: never surface on kiosk (V1). Inactive = Coming soon — still listed.
  if (apiProvider.status === 'maintenance') {
    return null;
  }
  const normalizedCode = apiProvider.code.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
  const tagRaw = apiProvider.clientTag;
  const clientTag =
    tagRaw != null && String(tagRaw).trim() !== '' ? String(tagRaw).trim().slice(0, 64) : undefined;

  return {
    id: apiProvider.id,
    code: normalizedCode,
    name: apiProvider.name,
    tagline: getTaglineForProvider(apiProvider.code, apiProvider.name),
    icon: getIconPathForProvider(apiProvider.code),
    status: apiProvider.status,
    depositType: apiProvider.config?.depositType || 'auto',
    ...(clientTag ? { clientTag } : {}),
    ...(apiProvider.minDeposit != null ? { minDeposit: Number(apiProvider.minDeposit) } : {}),
  };
};

/** True when provider is Coming soon (inactive) — visible but not actionable. */
export function isComingSoonProvider(status?: string | null): boolean {
  return status === 'inactive';
}

/** Home / play surfaces hide maintenance; service already strips them (defense in depth). */
export function excludeMaintenanceProviders<T extends { status?: string | null }>(items: T[]): T[] {
  return items.filter((i) => i.status !== 'maintenance');
}
